import { usePrivy } from "@privy-io/react-auth";
import {
  createRouter,
  RouterProvider as TanStackRouterProvider,
} from "@tanstack/react-router";
import { routeTree } from "@/routeTree.gen";
import { useEffect } from "react";
import { getSlideDirection } from "@/lib/configs/page-transition";
import { PixelErrorFallback } from "@/components/PixelErrorFallback";
import { PixelNotFound } from "@/components/PixelNotFound";
import posthog from "posthog-js";

// Define the router context type
export interface RouterContext {
  auth: {
    isAuthenticated: boolean;
    isReady: boolean;
    hasWallet: boolean;
  };
}

// Create the router with initial context
const router = createRouter({
  routeTree,
  context: {
    auth: {
      isAuthenticated: false,
      isReady: false,
      hasWallet: false,
    },
  },
  defaultErrorComponent: PixelErrorFallback,
  defaultNotFoundComponent: PixelNotFound,
  defaultViewTransition: {
    types: ({ fromLocation, toLocation }) => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return [];
      }

      const fromPath = fromLocation?.pathname ?? "";
      const toPath = toLocation.pathname;

      const dir = getSlideDirection(fromPath, toPath);
      return dir ? [dir] : [];
    },
  },
});

router.subscribe("onResolved", (evt) => {
  posthog.capture("$pageview", { $current_url: evt.toLocation.href });
});

// Type augmentation for route context
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouterProvider() {
  const { authenticated, ready, user } = usePrivy();

  // Update router context when auth state changes
  useEffect(() => {
    router.invalidate();
  }, [authenticated, ready, user?.wallet]);

  // Always update context before rendering
  router.update({
    context: {
      auth: {
        isAuthenticated: authenticated,
        isReady: ready,
        hasWallet: !!user?.wallet,
      },
    },
  });

  return <TanStackRouterProvider router={router} />;
}
